import { useState } from 'react'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { connectAccount } from '@/services/account'
import { useAuth } from '@/store/auth'

/**
 * What the desktop app shows instead of a login form.
 *
 * The app never sees a password. Signing in happens in a browser the person
 * already trusts and is probably already signed into, and they approve this
 * machine from there — which is also the moment the device gets paired, so one
 * action does both.
 */
export function ConnectAccountPage() {
  const restore = useAuth((s) => s.restore)
  const [waiting, setWaiting] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const connect = async () => {
    setError(null)
    setWaiting(true)
    try {
      await connectAccount(setUrl)
      // The session is stored; restore() loads the account behind it.
      await restore()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect this device.')
    } finally {
      setWaiting(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
      <h1 className="text-xl font-medium">NevTan AI</h1>
      <p className="mt-2 text-sm text-content-muted">
        {waiting
          ? 'Approve this device in your browser to finish connecting.'
          : 'Sign in to connect this device to your account. Models still run on this machine.'}
      </p>

      {waiting ? (
        <>
          <Loader2 size={22} className="mt-6 animate-spin text-accent" />
          {url && (
            // Shown as well as opened: a browser that does not launch should
            // not leave someone staring at a spinner with nothing to click.
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex items-center gap-1.5 text-xs text-accent hover:underline"
            >
              <ExternalLink size={12} /> Open the approval page
            </a>
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={() => void connect()}
          className="mt-6 w-full rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-content"
        >
          Sign in
        </button>
      )}

      {error && (
        <div className="mt-5 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/5 p-3 text-left text-sm text-danger">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}
    </div>
  )
}
